// TodayScreen — the home surface, restructured into three zones:
//
//   Zone A · Status — greeting, progress ring, streak, money/hours saved.
//                     For first-run users (no taper plan yet), the
//                     FirstRunGuide replaces this zone entirely.
//
//   Zone B · Now    — ONE priority card at a time. The resolver in
//                     useTodayPriority picks: MAT → hold → check-in → nudge.
//                     AnimatePresence cross-fades when the priority
//                     changes. Crisis banner is above the zones (always
//                     wins) and remains separate.
//
//   Quick log + today's doses + Talk-Me-Down sit between Zone B and
//   Zone C — they're frequent-use, not "tools."
//
//   Zone C · Tools  — collapsible drawer for the deeper utilities:
//                     PAWS week, urge surf, mind tricks, trends, setback.
//                     Footer below the drawer holds the prominent crisis
//                     link (always visible).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  BookOpen,
  Brain,
  ChevronDown,
  Download,
  LineChart,
  Mail,
  Pill,
  NotebookPen,
  Search,
  Sunrise,
  Waves,
} from 'lucide-react';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import { PageContainer } from '../../components/layout/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiErrorCard } from '../../components/ui/ApiErrorCard';
import { ListItemCard } from '../../components/ui/ListItemCard';
import { AINudgeCard } from '../../components/domain/AINudgeCard';
import { DoseChip, DosePresetChip } from '../../components/domain/DoseChip';
import { ProgressRing } from '../../components/domain/ProgressRing';
import { ConsistencyRing } from '../../components/domain/ConsistencyRing';
import { CrisisBanner } from '../../components/domain/CrisisBanner';
import { CrisisModal } from '../../components/domain/CrisisModal';
import { HandoffCard } from '../../components/domain/HandoffCard';
import { HoldSuggestionCard } from '../../components/domain/HoldSuggestionCard';
import { PAWSWeekCard } from '../../components/domain/PAWSWeekCard';
import { MATReferralCard, type MATRecommendation } from '../../components/domain/MATReferralCard';
import { TalkMeDownCard } from '../../components/domain/TalkMeDownCard';
import { SosToolkitCard } from '../../components/today/SosToolkitCard';
import { TomorrowPlanCard } from '../../components/domain/TomorrowPlanCard';
import { RoughNightToggle } from '../../components/domain/RoughNightToggle';
import { FeelCheckBanner, type PendingFollowup } from '../../components/domain/FeelCheckBanner';
import { MilestoneCard } from '../../components/domain/MilestoneCard';
import { FirstRunGuide } from './FirstRunGuide';
import { TodaysNote } from './TodaysNote';
import { BedtimePledgeCard } from './BedtimePledgeCard';
import { LapseRecoverySheet } from './LapseRecoverySheet';
import { NextDoseView } from './NextDoseView';
// Phase 10B hotfix — "Same as last" must read the SAME localStorage key
// the LogDoseSheet writes (qk.lastDose.v2). TodayScreen previously had
// its own readLastDose() that only read the legacy v1 key, so the
// quick-dose label went stale the moment a user logged via the sheet.
import { readLastDose, writeLastDose, type LastDose } from '../log/LogDoseSheet';
import { PAWSTomorrowCard } from './PAWSTomorrowCard';
import { StoryOfDayCard } from './StoryOfDayCard';
import { WhyICard } from './WhyICard';
import { MoodSleepCard } from '../../components/today/MoodSleepCard';
import { StretchTriad } from './StretchTriad';
import { PAWSPhaseCard } from './PAWSPhaseCard';
import { RelapsePreventionCard } from './RelapsePreventionCard';
import { DoseTimingCard } from '../../components/domain/DoseTimingCard';
import { ZoneAStatusSkeleton } from '../../components/today/ZoneAStatusSkeleton';
import { DeferredBaselineNudge } from '../../components/today/DeferredBaselineNudge';
import { TonightsCheckInCard } from '../../components/today/TonightsCheckInCard';
import { PredictedWindowChip } from '../../components/today/PredictedWindowChip';
import { WithdrawalProjectionCard } from './WithdrawalProjectionCard';
import { RecoveryForecastCard } from './RecoveryForecastCard';
import { useTodayPriority } from './useTodayPriority';
import { WeeklyDigest, type WeeklyDigestData } from '../../components/domain/WeeklyDigest';
import { useAuth } from '../../auth/AuthContext';
import {
  isDeferredMode,
  isNudgeDismissedToday,
  dismissNudgeForToday,
} from '../../lib/deferredBaseline';
import { pawsForWeek } from '../../content/pawsCalendar';
import { Icon } from '../../components/ui/Icon';
import { useSaveDataMode } from '../../hooks/useSaveDataMode';
import { useHonestDay } from '../../hooks/useHonestDay';
import { useRoughNight } from '../../hooks/useRoughNight';
import { useTomorrowPlan } from '../../hooks/useTomorrowPlan';
import { apiDelete, apiGet, apiPost, apiPut, ApiClientError } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';
import { haptics } from '../../lib/haptics';
import { usePullToRefresh } from '../../lib/usePullToRefresh';
import { useInstallPrompt, useCanShowIosInstall } from '../../lib/useInstallPrompt';
import { useToast } from '../../components/ui/Toast';
import { EASE_OUT, useReducedMotion } from '../../lib/motion';
import { useTimeOfDay } from '../../lib/useTimeOfDay';
import { fmtDate, ymdAddDays } from '../../lib/format';
import { useToday } from '../../lib/useToday';
import { COMMON_TABLET_STRENGTHS_MG } from '@shared/constants';
import type {
  Dose,
  DailyLog,
  TaperPlanWire,
  StreakInfo,
  MoneyTimeSaved,
  HandoffRow,
  LettersResponse,
} from '@shared/types';

// ---------------------------------------------------------------------
// TanStack Query adapter — internal to TodayScreen.tsx.
//
// The screen mounts ~15 read queries on every render. Migrating from
// the old useApi() (per-call useState + useEffect, no dedup, no cache)
// to useQuery() unlocks:
//   1. Cross-mount dedup: two screens that hit /api/taper share one
//      in-flight request and one cached payload.
//   2. Stale-while-revalidate: a quick back-navigation paints from
//      cache (staleTime = 60s in queryClient.ts), then refetches in
//      the background.
//   3. Coordinated invalidation: a single
//      `queryClient.invalidateQueries({ queryKey: ['today', ...] })`
//      can re-run an entire screen's worth of queries after a mutation.
//
// This adapter preserves the original useApi return shape
// (`{ status, data?, error?, refresh }`) so the rest of the screen
// body — the 1000+ lines that destructure `dosesRes.status`,
// `taperRes.data`, etc. — doesn't need to change. The win is in the
// cache layer, not the JSX.
//
// `enabled` mirrors the legacy `path == null → idle` semantic for the
// Save-Data deferral pattern (each `useApi(saveData ? null : '...')`
// now becomes `useTodayQuery([...], path, { enabled: !saveData })`).
//
// Cite: TanStack Query v5 — useQuery + invalidateQueries docs.
// ---------------------------------------------------------------------
type TodayQueryState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; error: ApiClientError | Error };

function useTodayQuery<T>(
  key: readonly unknown[],
  path: string,
  options: { enabled?: boolean } = {},
): TodayQueryState<T> & { refresh: () => void } {
  const queryClient = useQueryClient();
  const enabled = options.enabled !== false;
  const query = useQuery<T, ApiClientError | Error>({
    queryKey: key,
    queryFn: () => apiGet<T>(path),
    enabled,
  });
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: key });
    // The key arg is a stable array passed in by the caller; we rely on
    // referential identity, not deep equality, so callers must define
    // their query keys outside renders (which they do — they're string
    // literals).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);
  if (!enabled) return { status: 'idle', refresh };
  if (query.status === 'pending') return { status: 'loading', refresh };
  if (query.status === 'error') return { status: 'error', error: query.error, refresh };
  return { status: 'ok', data: query.data, refresh };
}

interface PledgeRow {
  text: string;
  date: string;
}

// Honest one-liners that fire after a successful dose log. Picked at
// random per log. The voice is "Logged. Honest." — no "Great job!"
// patronizing. Mix neutral, validating, and gently identity-anchoring.
const HONEST_LOG_LINES = [
  'Logged. Honest.',
  'Logged. The data tells the story.',
  'Logged. We see you.',
  'Logged. You showed up.',
  'Logged. That is the practice.',
];
function pickHonestLine(): string {
  return HONEST_LOG_LINES[Math.floor(Math.random() * HONEST_LOG_LINES.length)];
}

interface NudgeResponse {
  message: string;
  generatedAt: string;
  model: string;
}
interface AdaptiveResponse {
  content: string;
  createdAt: string;
}

const TOOLS_OPEN_KEY = 'qk.todayToolsOpen.v1';

// LastDose + readLastDose + writeLastDose are imported from LogDoseSheet
// (the canonical qk.lastDose.v2 implementation). See the import block
// above for why the local v1-only copy was removed.

// Part 12 §P12.6 C2 — thin wrapper. Routes through to the full screen
// (`TodayScreenFull`) by default; redirects to the opt-in minimalist
// view when `users.settings.minimalistTodayEnabled === true`. Keeping
// the dispatch above the full screen's `use*` block obeys Rules of
// Hooks; the full screen's 19 useApi calls never mount when the user
// has opted out.
export function TodayScreen() {
  const { me } = useAuth();
  if (me?.settings.minimalistTodayEnabled === true) {
    return <NextDoseView />;
  }
  return <TodayScreenFull />;
}

function TodayScreenFull() {
  const navigate = useNavigate();
  const toast = useToast();
  const reduce = useReducedMotion();
  const { me, effectiveTz, refresh } = useAuth();
  const today = useToday();
  const isMobile = useIsMobile();
  // 72-hour crisis window: pull the last 4 calendar days of logs (today
  // plus 3 prior) so the CrisisBanner stays visible the full 72 h after
  // any C-SSRS ideation hit or crisis-resources surface. See the
  // `crisisWindowOpen` effect below.
  const crisisWindowFrom = ymdAddDays(today, -3);

  // Phase 27 — Save-Data hint. When the user (or their OS) signals
  // they want reduced data usage, defer non-safety-critical fetches
  // (pledges, weekly digest, handoff, letters, adaptive guidance,
  // streak / money) until they explicitly open the relevant card.
  // SAFETY surfaces (doses, taper, crisis-window logs, clinical
  // recommendation, danger-window predictor, today's check-in log,
  // dose follow-ups, AI check-in nudge) ALWAYS load eagerly — gating
  // them on a network hint is unacceptable for a recovery-context
  // app. Each `useApi(saveData ? null : '...')` skips the request
  // until saveData flips back to false (e.g. user opts out, OS Lite
  // mode toggled off, or the user opens the deferred surface manually).
  const saveData = useSaveDataMode();

  // Pass effectiveTz so the server interprets from/to as user-local
  // calendar days. Without this, the daily list would carry over the
  // prior evening's doses until UTC midnight (~7 h late in PDT).
  const tzQs = `&tz=${encodeURIComponent(effectiveTz)}`;
  // Top-level queryClient handle. Used by `quickLog`, `deleteDose`,
  // `sameAsLast`, `applyHold`, `consumeHandoff`, and `spendFreeze` to
  // invalidate the relevant query keys after a mutation lands —
  // replaces the prior scatter of `.refresh()` calls.
  const queryClient = useQueryClient();
  // SAFETY-CRITICAL: always-eager fetches. Query keys are stable
  // string-array tuples; param-dependent keys include the param so
  // each calendar day / tz combo is cached separately rather than
  // colliding with yesterday's data.
  const dosesRes = useTodayQuery<Dose[]>(
    ['today', 'doses', today, effectiveTz],
    `/api/doses?from=${today}&to=${today}${tzQs}`,
  );
  const followupsRes = useTodayQuery<PendingFollowup[]>(
    ['today', 'doses', 'followups'],
    `/api/doses/followups`,
  );
  const taperRes = useTodayQuery<TaperPlanWire | null>(['today', 'taper'], '/api/taper');
  const logRes = useTodayQuery<DailyLog | null>(['today', 'log', today], `/api/logs/${today}`);
  // Range fetch for the crisis window predicate. The API returns the
  // camelCase shape via rowToLog; we widen DailyLog locally with the two
  // C-SSRS fields the API exposes but `shared/types.ts` hasn't yet typed.
  const logsRes = useTodayQuery<DailyLog[]>(
    ['today', 'logs', 'crisisWindow', crisisWindowFrom, today],
    `/api/logs?from=${crisisWindowFrom}&to=${today}`,
  );
  const nudgeRes = useTodayQuery<NudgeResponse | null>(
    ['today', 'ai', 'checkin'],
    '/api/ai/checkin',
  );
  const clinicalRes = useTodayQuery<MATRecommendation & { evaluatedAt: string }>(
    ['today', 'clinical', 'recommendation'],
    '/api/clinical/recommendation',
  );
  // NON-ESSENTIAL: deferrable under Save-Data.
  const streakRes = useTodayQuery<StreakInfo>(['today', 'me', 'streak'], '/api/me/streak', {
    enabled: !saveData,
  });
  const moneyRes = useTodayQuery<MoneyTimeSaved>(
    ['today', 'insights', 'moneyTimeSaved'],
    '/api/insights/money-time-saved',
    { enabled: !saveData },
  );
  // Part 6 — pledge surfaces. yesterdayPledgeRes feeds TodaysNote's
  // morning contextual line; todayPledgeRes gates the BedtimePledgeCard.
  const yesterdayPledgeRes = useTodayQuery<PledgeRow | null>(
    ['today', 'pledge', 'yesterday'],
    '/api/pledge/yesterday',
    { enabled: !saveData },
  );
  const todayPledgeRes = useTodayQuery<PledgeRow | null>(['today', 'pledge'], '/api/pledge', {
    enabled: !saveData,
  });
  // Adaptive guidance: cron writes ai_messages rows; we finally fetch them.
  // Phase B §P10.6 passes the 72-hour window explicitly so the endpoint's
  // clamp behavior is visible at the call site (the default also is 72h,
  // but stating it here keeps the contract obvious during code review).
  const adaptiveLatestRes = useTodayQuery<AdaptiveResponse | null>(
    ['today', 'ai', 'adaptiveLatest', 72],
    '/api/ai/adaptive-latest?max_age_hours=72',
    { enabled: !saveData },
  );
  // Phase 27.2 — Sunday weekly digest. Wins Zone B on Sun + Mon.
  const digestRes = useTodayQuery<WeeklyDigestData & { generatedAt: string | null }>(
    ['today', 'ai', 'digest'],
    '/api/ai/digest',
    { enabled: !saveData },
  );
  // Phase C.1 §P10.5 — Quiet Hand-Off. Most-recent unexpired,
  // unconsumed note from yesterday-you to today-you. The server filters
  // on `consumed_at IS NULL AND expires_at > now`, so `null` here means
  // there's nothing to surface in Zone B.
  const handoffRes = useTodayQuery<HandoffRow | null>(['today', 'handoff'], '/api/handoff', {
    enabled: !saveData,
  });
  // Phase C.2 §P10.7 — Letters to Future You. We only care here about
  // the `delivered` bucket (delivered_at !== null AND dismissed_at ===
  // null); the LettersScreen owns the full list. A non-empty
  // `delivered` array is what qualifies the 'letter' priority for
  // Zone B; the actual playback lives on /app/letters so we keep
  // Zone B as a compact teaser.
  const lettersRes = useTodayQuery<LettersResponse | null>(['today', 'letters'], '/api/letters', {
    enabled: !saveData,
  });
  // Phase 5 PR-B §F2 — predicted-window chip. Low-priority surface; the
  // server returns withinSixHours=true when the next forecasted danger
  // window is inside the 6h horizon, and renders nothing otherwise.
  const dangerWindowRes = useTodayQuery<{
    window: { hour: number; weekday: number; label: string; confidence: number } | null;
    withinSixHours: boolean;
  }>(['today', 'me', 'nextDangerWindow'], '/api/me/next-danger-window');
  const period = useTimeOfDay();
  // LapseRecoverySheet opens automatically when today first crosses
  // target. We dedupe via localStorage so re-loading the page doesn't
  // re-prompt the user. Manual reopen is via the "I had a setback"
  // tool entry (which clears the dedupe flag).
  const [lapseOpen, setLapseOpen] = useState(false);
  // Freeze spend state.
  const [freezeBusy, setFreezeBusy] = useState(false);
  // Phase C.1 — "Thanks, past me" tap state. POST /api/handoff/:id is
  // a single round-trip; we surface the loading state on the button
  // so a slow network doesn't make the dismiss feel ignored.
  const [consumingHandoff, setConsumingHandoff] = useState(false);

  // Phase 2 — PWA install affordance inside the Tools drawer. Chrome /
  // Edge / Android: `canInstall` flips true once the `beforeinstallprompt`
  // event is captured (listener lives in src/lib/useInstallPrompt at
  // module scope, imported from main.tsx). iOS Safari has no such
  // event, so we fall back to a text instruction.
  const install = useInstallPrompt();
  const canShowIosInstall = useCanShowIosInstall();

  // Pull-to-refresh attaches to the PageContainer's outer div. We
  // forward the ref into PageContainer so the gesture listener has
  // the real scroll element (otherwise `scrollTop === 0` would never
  // be satisfied on a non-scrollable wrapper).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const ptr = usePullToRefresh(scrollRef, {
    onRefresh: async () => {
      // Fire haptic on cross-threshold, not on every move event, so
      // it matches the user's "I committed to refresh" gesture.
      haptics.confirm();
      // We don't refresh all 19 useApi results — most are derived /
      // niche. The five below cover the at-a-glance Today fold:
      //   - doses: today's dose list
      //   - streak: streak header + consistency ring
      //   - milestone surfaces via taper + streak; refresh both
      //   - taper: today's target / progress ring
      //   - money: saved + reclaimed strip
      // The other 14 calls (followups, logs, nudge, pledge, adaptive,
      // digest, handoff, letters, clinical, etc.) refresh on their
      // own cadence and don't need to be tied to the pull gesture.
      await Promise.all([
        Promise.resolve(dosesRes.refresh()),
        Promise.resolve(streakRes.refresh()),
        Promise.resolve(taperRes.refresh()),
        Promise.resolve(moneyRes.refresh()),
        Promise.resolve(logRes.refresh()),
      ]);
    },
  });

  const [crisisOpen, setCrisisOpen] = useState(false);
  const [snoozingBanner, setSnoozingBanner] = useState(false);

  // PWA shortcut wire-up: the "Crisis resources" manifest shortcut
  // points at `/app#crisis` so a user can launch the OS-level
  // shortcut from a long-press on the home-screen icon. We open the
  // CrisisModal directly when we see that hash on mount and clear it
  // so a refresh doesn't auto-reopen. Web Manifest shortcuts spec:
  // https://w3c.github.io/manifest/#shortcuts-member
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#crisis') return;
    setCrisisOpen(true);
    // Replace state so a refresh / share doesn't reopen indefinitely.
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      // best-effort — older browsers may throw on replaceState
    }
  }, []);
  const [adaptive, setAdaptive] = useState<AdaptiveResponse | null>(null);
  const [matDismissed, setMatDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(`qk.matDismissed.${today}`) === '1';
  });
  // Tools drawer is sticky per-session via localStorage. Default closed
  // so a new user isn't dropped into a wall of tools.
  const [toolsOpen, setToolsOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(TOOLS_OPEN_KEY) === '1';
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(TOOLS_OPEN_KEY, toolsOpen ? '1' : '0');
    } catch {
      // best-effort
    }
  }, [toolsOpen]);

  // CrisisBanner is derived per-render (Part 12 §P12.7 D4) — the
  // prior `setBannerVisible(true)` pattern was sticky-true and never
  // cleared when the 72-h window closed. Two predicates ride together
  // at render time below:
  //   1. crisisWindowOpen — any log in last 72 h with ssrsLevel >= 1 or
  //      crisisResourcesShown = 1.
  //   2. snoozedUntil — user tapped "Snooze 24h"; if `now < snoozedUntil`
  //      the banner hides; once the timestamp passes, predicate 1
  //      re-asserts itself naturally.

  // Hydrate adaptive guidance from the cron-written ai_messages rows.
  // When a fresh hold suggestion exists in the last 72h, surface it via
  // useTodayPriority's 'hold' branch.
  const adaptiveData = adaptiveLatestRes.status === 'ok' ? adaptiveLatestRes.data : undefined;
  useEffect(() => {
    if (adaptiveData === undefined) return;
    if (adaptiveData === null) {
      setAdaptive(null);
      return;
    }
    setAdaptive({
      content: adaptiveData.content,
      createdAt: adaptiveData.createdAt,
    });
  }, [adaptiveData]);

  const todayTarget = useMemo(() => {
    if (taperRes.status !== 'ok' || !taperRes.data) return 0;
    return taperRes.data.days.find((d) => d.date === today)?.targetMg ?? 0;
  }, [taperRes, today]);

  const todayActual = useMemo(() => {
    if (dosesRes.status !== 'ok') return 0;
    return Math.round(dosesRes.data.reduce((a, d) => a + d.computedMg7oh, 0) * 10) / 10;
  }, [dosesRes]);

  const taperDayIndex = useMemo(() => {
    if (taperRes.status !== 'ok' || !taperRes.data) return null;
    const todayDay = taperRes.data.days.find((d) => d.date === today);
    return todayDay ? todayDay.dayIndex : null;
  }, [taperRes, today]);
  const pawsWeek = taperDayIndex != null ? pawsForWeek(Math.floor(taperDayIndex / 7)) : null;
  // Phase 21 — tomorrow's PAWS week, for the evening predictive card.
  const tomorrowDayIndex = taperDayIndex != null ? taperDayIndex + 1 : null;
  const pawsTomorrow =
    tomorrowDayIndex != null ? pawsForWeek(Math.floor(tomorrowDayIndex / 7)) : null;

  // Shared invalidation set for dose-mutation handlers (log / delete /
  // same-as-last). One queryClient.invalidateQueries per key replaces
  // the prior `.refresh()` scatter and ensures any other mounted
  // consumer of the same key (e.g. an Insights screen open in a back
  // stack) also refetches.
  const invalidateAfterDose = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['today', 'doses'] });
    void queryClient.invalidateQueries({ queryKey: ['today', 'me', 'streak'] });
    void queryClient.invalidateQueries({ queryKey: ['today', 'insights', 'moneyTimeSaved'] });
  }, [queryClient]);

  async function quickLog(amountMg: number) {
    try {
      await apiPost('/api/doses', {
        form: 'tablet_7oh',
        amount: amountMg,
        amountUnit: 'mg',
      });
      // Persist to the canonical qk.lastDose.v2 key (with a timestamp)
      // so the "Same as last" label + the repeat-dose card both reflect
      // this quick-log immediately.
      writeLastDose({
        form: 'tablet_7oh',
        amount: amountMg,
        ts: new Date().toISOString(),
      });
      haptics.confirm();
      toast.push(pickHonestLine());
      invalidateAfterDose();
    } catch (err) {
      if (isQueuedOffline(err)) {
        toast.push("Saved offline — we'll sync when you're back online.");
      } else {
        toast.push(apiErrorMessage(err, 'Could not log — try again.'), { tone: 'error' });
      }
    }
  }

  // Optimistic-delete + 5s undo flow.
  // The dose disappears from the rendered list immediately (via
  // `pendingDeleteIds`) and we hold off the server DELETE for 5s so an
  // accidental tap can be undone. The cancelled flag lives on a ref keyed
  // by dose id so two rapid deletes can be cancelled independently.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<number>>(new Set());
  const undoFlagsRef = useRef<Map<number, { cancelled: boolean }>>(new Map());
  const UNDO_WINDOW_MS = 5000;

  const removePending = useCallback((id: number) => {
    setPendingDeleteIds((cur) => {
      if (!cur.has(id)) return cur;
      const next = new Set(cur);
      next.delete(id);
      return next;
    });
    undoFlagsRef.current.delete(id);
  }, []);

  async function deleteDose(id: number) {
    // If the same dose is somehow tapped twice during its undo window,
    // ignore the second tap — the first one owns the timer.
    if (pendingDeleteIds.has(id)) return;

    const flag = { cancelled: false };
    undoFlagsRef.current.set(id, flag);
    setPendingDeleteIds((cur) => {
      const next = new Set(cur);
      next.add(id);
      return next;
    });
    haptics.warn();
    toast.push('Dose removed', {
      durationMs: UNDO_WINDOW_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          flag.cancelled = true;
          removePending(id);
        },
      },
    });

    await new Promise((r) => setTimeout(r, UNDO_WINDOW_MS));
    if (flag.cancelled) return;

    try {
      await apiDelete(`/api/doses/${id}`);
      invalidateAfterDose();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not delete — try again.'), { tone: 'error' });
      }
    } finally {
      // Resync optimistic state from authority either way.
      removePending(id);
    }
  }

  const lastDose: LastDose | null = readLastDose();
  const sameAsLast = useCallback(async () => {
    if (!lastDose) return;
    const unit = lastDose.form === 'tablet_7oh' ? 'mg' : lastDose.form === 'leaf' ? 'g' : 'mL';
    try {
      await apiPost('/api/doses', {
        form: lastDose.form,
        amount: lastDose.amount,
        amountUnit: unit,
        concentrationMgPerMl: lastDose.form === 'extract' ? lastDose.concentration : undefined,
        strain: lastDose.form === 'leaf' ? lastDose.strain : undefined,
      });
      // Re-stamp the canonical key so the repeat-dose recency heuristic
      // (12h window) and the "Same as last" label both stay fresh.
      writeLastDose({ ...lastDose, ts: new Date().toISOString() });
      haptics.confirm();
      toast.push(pickHonestLine());
      invalidateAfterDose();
    } catch (err) {
      if (isQueuedOffline(err)) {
        toast.push("Saved offline — we'll sync when you're back online.");
      } else {
        toast.push(apiErrorMessage(err, 'Could not log — try again.'), { tone: 'error' });
      }
    }
  }, [lastDose, invalidateAfterDose, toast]);

  async function applyHold() {
    try {
      await apiPost('/api/taper/hold', { fromDate: today, days: 2, reason: 'sows_high' });
      toast.push('2-day hold applied');
      void queryClient.invalidateQueries({ queryKey: ['today', 'taper'] });
      setAdaptive(null);
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not apply hold.'), { tone: 'error' });
      }
    }
  }

  const checkInDone = logRes.status === 'ok' && logRes.data !== null;
  const todayPledgeText =
    todayPledgeRes.status === 'ok' ? (todayPledgeRes.data?.text ?? null) : null;
  const yesterdayPledgeText =
    yesterdayPledgeRes.status === 'ok' ? (yesterdayPledgeRes.data?.text ?? null) : null;
  // Phase D.1 §P10.9 — Rough-Night soften toggle. While active, the
  // Today screen dims celebratory surfaces (consistency pulse, money
  // and hours-saved counters) and swaps the greeting to a non-measuring
  // tone. Support surfaces (Urge-surf, Breathe, Crisis) stay full
  // opacity — this flag never gates them.
  const { active: roughNight } = useRoughNight();
  // Time-of-day greeting. The 3am-Mode lite (Phase 19) swaps the night
  // greeting from "Late night" to a softer "You're awake. That's okay."
  // The data-period attribute on body powers the dim treatment below.
  // Phase D.1 §P10.9 — rough-night soften swaps the greeting to a
  // non-measuring tone while active.
  const greeting = useMemo(() => {
    if (roughNight) return 'Tonight is for getting through.';
    if (period === 'night') return "You're awake. That's okay.";
    if (period === 'morning') return 'Morning';
    if (period === 'day') return 'Afternoon';
    return 'Evening';
  }, [period, roughNight]);

  // Apply data-period on <html> so the night-mode CSS hooks can switch
  // background + the details opacity in a single rule set.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-period', period);
    return () => {
      // Don't unset on unmount — other screens may want to read it too.
    };
  }, [period]);

  // Primary CTA picker for the TodaysNote card. Returns null when the
  // best move is "no decision needed" — TodaysNote then renders without
  // a CTA and the user browses the rest of the screen on their own.
  const todaysNoteCta = useMemo<{ label: string; onClick: () => void } | null>(() => {
    if (period === 'night') {
      return { label: 'Breathe with me · 60s', onClick: () => navigate('/app/breathe') };
    }
    if (period === 'evening' && !checkInDone) {
      return { label: "Tonight's check-in", onClick: () => navigate('/app/checkin') };
    }
    if (period === 'morning' && yesterdayPledgeText) {
      // Yesterday's pledge IS the morning message — don't compete with
      // a CTA. The user can still scroll to quick-log.
      return null;
    }
    return {
      label: 'Quick log →',
      onClick: () => {
        const el = document.getElementById('quick-log');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    };
  }, [period, checkInDone, yesterdayPledgeText, navigate]);

  // BedtimePledgeCard renders only between 20:00–02:00 local, when no
  // pledge has been written for today yet, and the user isn't in
  // first-run state. Keeps the pledge nudge out of the morning, where
  // yesterday's pledge already does the work.
  const showBedtimePledge =
    !todayPledgeText &&
    (period === 'evening' || period === 'night') &&
    todayPledgeRes.status === 'ok';

  const money = moneyRes.status === 'ok' ? moneyRes.data : null;

  // First-run detection: no taper plan yet means baseline not set.
  // Once a plan exists, we're past first run.
  const planLoading = taperRes.status === 'loading';
  const isFirstRun =
    taperRes.status === 'ok' && (taperRes.data == null || taperRes.data.days.length === 0);

  // Phase 3 #15 — deferred-baseline mode. The user picked "Just log
  // for now" in onboarding; FirstRunGuide renders the quick-log chip
  // strip, and after the first dose lands we surface a friendly
  // nudge to come back and finish baseline + plan-shape.
  //
  // The nudge is dismissible per-day; the tick counter forces a
  // re-render after dismissNudgeForToday() so the card actually
  // disappears immediately (otherwise the next render reads stale
  // localStorage). Re-surfaces tomorrow or once doseCount >= 5,
  // whichever comes first — the dismissed-today predicate checks
  // against today's YMD, not a generic "dismissed" flag.
  const deferredMode = isDeferredMode() && isFirstRun;
  const [nudgeDismissedTick, setNudgeDismissedTick] = useState(0);
  const doseCount = dosesRes.status === 'ok' ? dosesRes.data.length : 0;
  const nudgeDismissed = isNudgeDismissedToday(today);
  // Tick is referenced so the dependency-on-render hook ordering
  // stays stable; the value itself doesn't gate the predicate (the
  // localStorage check does). ESLint would warn on an unused var.
  void nudgeDismissedTick;
  const showDeferredNudge = deferredMode && doseCount >= 1 && (!nudgeDismissed || doseCount >= 5);

  // PAWS-tomorrow is "ready" when we know tomorrow's week AND the user
  // has logged at least one dose (proxy for "has been engaging" — we
  // don't want this surface to appear on a fresh, empty account).
  const pawsTomorrowReady =
    pawsTomorrow != null && dosesRes.status === 'ok' && dosesRes.data.length > 0;

  // Phase B §P10.6 — honest-day suppression. Compute on every render
  // off the already-resolved actual/target. Null while either side is
  // loading; the consumers (`useTodayPriority`, MotivationalToast, the
  // rings, WeeklyDigest) treat null as "don't suppress yet."
  const honestDayActual = dosesRes.status === 'ok' ? todayActual : null;
  const honestDayTarget = taperRes.status === 'ok' ? todayTarget : null;
  const honestDay = useHonestDay(honestDayActual, honestDayTarget);

  // Priority resolver for Zone B.
  const nowDate = new Date();
  // Timezone for the digest-window check — settings value with a sane
  // fallback if the row doesn't yet have one (Phase B §P10.6 wires
  // /api/auth/me to default-fill this; older sessions get the fallback
  // until they refresh).
  const userTimezone = me?.settings.timezone ?? 'America/New_York';
  const priority = useTodayPriority({
    matSuggested:
      clinicalRes.status === 'ok' &&
      (clinicalRes.data.matSuggested || clinicalRes.data.inpatientSuggested),
    matDismissed,
    hasAdaptiveHold: adaptive != null,
    checkInDone,
    hour: nowDate.getHours(),
    weekday: nowDate.getDay(),
    nudgeMessage: nudgeRes.status === 'ok' ? (nudgeRes.data?.message ?? null) : null,
    nudgeReady: nudgeRes.status === 'ok',
    pawsTomorrowReady,
    digestGeneratedAt: digestRes.status === 'ok' ? (digestRes.data?.generatedAt ?? null) : null,
    nudgeOptIn: me?.settings.notifyMorning !== false,
    handoffAvailable: handoffRes.status === 'ok' && handoffRes.data !== null,
    letterDelivered: lettersRes.status === 'ok' && (lettersRes.data?.delivered.length ?? 0) > 0,
    userTimezone,
    honestDay: honestDay === true,
  });

  // Loading shape for the primary progress card.
  const progressLoading = dosesRes.status === 'loading' || taperRes.status === 'loading';
  const progressError =
    dosesRes.status === 'error'
      ? dosesRes.error
      : taperRes.status === 'error'
        ? taperRes.error
        : null;
  // Mobile reorder — `progressResolved` means the Zone A status area has
  // real data, so its four loaded cards (TodaysNote, recovery, insights,
  // "how today's going") can scatter to their own mobile slots.
  const progressResolved = !planLoading && !isFirstRun && !progressError && !progressLoading;

  // Lapse-sheet auto-open when today first goes over target.
  // De-duped per calendar day via localStorage. Phase 18.
  useEffect(() => {
    if (
      dosesRes.status === 'ok' &&
      taperRes.status === 'ok' &&
      todayTarget > 0 &&
      todayActual > todayTarget &&
      !isFirstRun
    ) {
      const key = `qk.lapseShown.${today}`;
      if (typeof window === 'undefined') return;
      if (window.localStorage.getItem(key) === '1') return;
      try {
        window.localStorage.setItem(key, '1');
      } catch {
        // ignored — best-effort
      }
      setLapseOpen(true);
    }
  }, [dosesRes.status, taperRes.status, todayActual, todayTarget, isFirstRun, today]);

  async function consumeHandoff() {
    if (handoffRes.status !== 'ok' || !handoffRes.data) return;
    const id = handoffRes.data.id;
    setConsumingHandoff(true);
    try {
      await apiPost(`/api/handoff/${id}`, { action: 'consume' });
      void queryClient.invalidateQueries({ queryKey: ['today', 'handoff'] });
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not dismiss — try again.'), { tone: 'error' });
      }
    } finally {
      setConsumingHandoff(false);
    }
  }

  async function spendFreeze() {
    setFreezeBusy(true);
    try {
      await apiPost('/api/me/use-streak-freeze', {});
      toast.push('Freeze used. You showed up. That counts.');
      void queryClient.invalidateQueries({ queryKey: ['today', 'me', 'streak'] });
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not use freeze.'), { tone: 'error' });
      }
    } finally {
      setFreezeBusy(false);
    }
  }

  // Banner visibility = (any crisis-window row) AND (not currently
  // snoozed). Recomputed every render so a snooze expiring at 24h
  // surfaces the banner again on the next render without state cleanup.
  const crisisWindowOpen =
    logsRes.status === 'ok' &&
    (logsRes.data ?? []).some(
      (r) => (r.ssrsIdeationLevel ?? 0) >= 1 || r.crisisResourcesShown === 1,
    );
  const snoozedUntilStr = me?.settings.crisisBannerSnoozedUntil;
  const snoozedActive =
    typeof snoozedUntilStr === 'string' && Date.parse(snoozedUntilStr) > Date.now();
  const bannerVisible = crisisWindowOpen && !snoozedActive;

  async function snoozeBanner() {
    setSnoozingBanner(true);
    try {
      const until = new Date(Date.now() + 24 * 60 * 60_000).toISOString();
      await apiPut('/api/me/settings', { crisisBannerSnoozedUntil: until });
      await refresh();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not snooze — try again.'), { tone: 'error' });
      }
    } finally {
      setSnoozingBanner(false);
    }
  }

  // ===== Home-screen section blocks =====
  // Each section below is built once, then assembled by `body` into a
  // first-run / mobile / desktop ordering. Mobile is the usage-ranked
  // reorder; desktop keeps the 2-column `today-zones` grid; first-run
  // is unchanged.

  // Pull-to-refresh indicator: shown during the pull and the refresh
  // request so the gesture has feedback; height 0 when idle.
  const ptrSpinner =
    (ptr.isPulling && ptr.pullDistance > 0) || ptr.isRefreshing ? (
      <div
        aria-live="polite"
        aria-label={ptr.isRefreshing ? 'Refreshing' : 'Pull to refresh'}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          blockSize: ptr.isRefreshing ? 24 : Math.min(ptr.pullDistance, 32),
          color: 'var(--color-accent)',
          transition: ptr.isRefreshing ? 'block-size 0.18s ease-out' : 'none',
          opacity: ptr.isRefreshing ? 1 : Math.min(1, ptr.pullDistance / 64),
        }}
      >
        <span
          className="btn-spinner"
          aria-hidden="true"
          style={{
            display: 'inline-block',
            inlineSize: 20,
            blockSize: 20,
            border: '2px solid currentColor',
            borderInlineEndColor: 'transparent',
            borderRadius: '50%',
          }}
        />
      </div>
    ) : null;

  // Tomorrow's plan — collapsed to a one-line summary on mobile, inline
  // on desktop. Self-hides via 404 for first-run users.
  const tomorrowPlan = !isFirstRun ? <TomorrowPlanCollapsible /> : null;
  // Zone A loaded cards — extracted individually so the mobile reorder
  // can place TodaysNote up top and scatter the rest lower.
  const todaysNote = (
    <TodaysNote
      actual={todayActual}
      target={todayTarget}
      period={period}
      yesterdaysPledge={yesterdayPledgeText}
      cta={todaysNoteCta}
    />
  );

  // "Your recovery" tap card — $ saved + hours not in withdrawal.
  // `null` until progress data resolves so it never sits beside a
  // loading skeleton on mobile.
  const recoveryCard =
    progressResolved && money && money.baselineMg ? (
      <Card className="recovery-link-strip" data-rough-night={roughNight ? 'true' : undefined}>
        <button
          type="button"
          className="recovery-link-card"
          onClick={() => navigate('/app/recovery')}
          aria-label="Open your recovery dashboard"
        >
          <div>
            <p className="text-caption muted" style={{ margin: 0 }}>
              Your recovery
            </p>
            <div className="recovery-link-card__numbers">
              <div>
                <p className="stat-card-value" style={{ margin: 0 }}>
                  {`$${money.dollarsSaved.toFixed(0)}`}
                </p>
                <p className="stat-card-caption muted" style={{ margin: 0 }}>
                  saved
                </p>
              </div>
              <div>
                <p className="stat-card-value" style={{ margin: 0 }}>
                  {`${money.hoursReclaimed.toFixed(0)}h`}
                </p>
                <p className="stat-card-caption muted" style={{ margin: 0 }}>
                  not in withdrawal
                </p>
              </div>
            </div>
            {taperDayIndex != null ? (
              <p className="text-caption muted" style={{ margin: 'var(--space-1) 0 0' }}>
                Day {taperDayIndex}
                {pawsWeek ? ` · ${pawsWeek.title.split(' — ')[0]}` : ''}
              </p>
            ) : null}
          </div>
          <span className="recovery-link-card__chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </Card>
    ) : null;

  // One-tap card into the insights surface.
  const insightsCard = progressResolved ? (
    <ListItemCard
      icon={LineChart}
      iconTone="primary"
      title="Your insights"
      caption="SOWS, mood, sleep, craving windows, substitutions."
      onClick={() => navigate('/app/insights')}
      trailing={<span aria-hidden="true">→</span>}
    />
  ) : null;

  // "How today's going" expander — progress ring, streak, stretch triad.
  const howTodayDetails = progressResolved ? (
    <details className="todays-note-details">
      <summary className="todays-note-details-summary">
        <span>How today's going</span>
        <Icon icon={ChevronDown} size={18} className="todays-note-details-chevron" />
      </summary>
      <div className="todays-note-details-body">
        <div className="row" style={{ alignItems: 'center', gap: 'var(--space-5)' }}>
          <ProgressRing actual={todayActual} target={todayTarget} />
          <div style={{ flex: 1 }}>
            <div className="text-caption muted">Today</div>
            <div className="todays-note-details-value">
              {todayActual.toFixed(1)} <span className="dim">/ {todayTarget.toFixed(1)} mg</span>
            </div>
          </div>
          <ConsistencyRing
            streak={streakRes.status === 'ok' ? streakRes.data : null}
            loading={streakRes.status === 'loading'}
            suppressCelebration={honestDay === true || roughNight}
            onProtectToday={
              streakRes.status === 'ok' &&
              todayActual > todayTarget &&
              streakRes.data.freezesRemaining > 0
                ? () => void spendFreeze()
                : undefined
            }
            protectBusy={freezeBusy}
          />
        </div>
        {/* PR 1B — slip-tolerant accounting triad. Renders below the
            rings; hides itself for first-week users with no stretches. */}
        <StretchTriad streak={streakRes.status === 'ok' ? streakRes.data : null} />
      </div>
    </details>
  ) : null;

  // Zone A status block: skeleton / first-run guide / error / skeleton /
  // loaded. The loaded branch reassembles the four cards above for the
  // desktop + first-run trees; the mobile tree places them individually.
  const zoneAStatus = planLoading ? (
    <ZoneAStatusSkeleton />
  ) : isFirstRun ? (
    <>
      <FirstRunGuide
        baselineLocked={false}
        hasAnyDose={dosesRes.status === 'ok' && dosesRes.data.length > 0}
        checkInDone={checkInDone}
        deferredMode={deferredMode}
        onQuickLog={deferredMode ? quickLog : undefined}
      />
      {/* Phase 3 #15 — friendly nudge after the first dose lands in
          deferred-baseline mode. Dismissible per-day; re-surfaces
          tomorrow or once the user has logged 5 doses. */}
      {deferredMode && showDeferredNudge ? (
        <DeferredBaselineNudge
          onDismiss={() => {
            dismissNudgeForToday(today);
            setNudgeDismissedTick((t) => t + 1);
          }}
          onSetUpPlan={() => navigate('/app/onboarding')}
        />
      ) : null}
    </>
  ) : progressError ? (
    <ApiErrorCard
      error={progressError}
      onRetry={() => {
        dosesRes.refresh();
        taperRes.refresh();
      }}
    />
  ) : progressLoading ? (
    <ZoneAStatusSkeleton />
  ) : (
    <div>
      {todaysNote}
      {recoveryCard}
      {insightsCard}
      {howTodayDetails}
    </div>
  );

  // Mobile slot 2: TodaysNote alone once data resolves; otherwise the
  // skeleton / error card (first-run never reaches the mobile branch).
  const statusSlot = progressResolved ? todaysNote : zoneAStatus;

  // Recovery Forecast — a daily "weather report" card (Feature 2). The
  // card self-hides on low data; gated to non-first-run.
  const recoveryForecastCard = !isFirstRun ? <RecoveryForecastCard /> : null;

  // MoodSleepCard + WhyICard — render for every authed user regardless
  // of taper-plan / first-run state.
  const moodSleep = <MoodSleepCard />;
  const whyI = <WhyICard />;

  // Zone B — the single "now" priority card; AnimatePresence cross-fades
  // when the resolved priority changes.
  const zoneB = !isFirstRun ? (
    <AnimatePresence mode="wait">
      {priority === 'mat' && clinicalRes.status === 'ok' ? (
        <motion.div
          key="mat"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <MATReferralCard
            rec={clinicalRes.data}
            adaptiveGuidance={adaptive?.content}
            adaptiveGeneratedAt={adaptive?.createdAt}
            onDismiss={() => {
              try {
                window.localStorage.setItem(`qk.matDismissed.${today}`, '1');
              } catch {
                // ignored — best-effort
              }
              setMatDismissed(true);
            }}
          />
        </motion.div>
      ) : priority === 'hold' && adaptive ? (
        <motion.div
          key="hold"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <HoldSuggestionCard
            message={adaptive.content}
            adaptiveGuidance={adaptive.content}
            adaptiveGeneratedAt={adaptive.createdAt}
            onApply={applyHold}
            onDismiss={() => setAdaptive(null)}
          />
        </motion.div>
      ) : priority === 'letter' &&
        lettersRes.status === 'ok' &&
        lettersRes.data &&
        lettersRes.data.delivered.length > 0 ? (
        <motion.div
          key="letter"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          {(() => {
            const letter = lettersRes.data.delivered[0];
            // Days-ago against the original write timestamp gives
            // the "from N days ago" framing the user remembers,
            // not the deliver-at they may not consciously recall.
            const created = new Date(letter.createdAt).getTime();
            const daysAgo = Math.max(1, Math.round((Date.now() - created) / 86_400_000));
            const extra = lettersRes.data.delivered.length - 1;
            return (
              <Card accentLeft>
                <div className="row" style={{ alignItems: 'center', gap: 'var(--space-3)' }}>
                  <Icon icon={Mail} size={20} />
                  <h3 className="text-h3" style={{ margin: 0 }}>
                    A letter from {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                  </h3>
                </div>
                <p
                  className="muted"
                  style={{ marginBlockStart: 'var(--space-2)', marginBlockEnd: 'var(--space-3)' }}
                >
                  Past-you picked today on purpose.
                  {extra > 0 ? ` (${extra} more waiting.)` : ''}
                </p>
                <Button variant="primary" onClick={() => navigate('/app/letters')}>
                  Open letter →
                </Button>
              </Card>
            );
          })()}
        </motion.div>
      ) : priority === 'handoff' && handoffRes.status === 'ok' && handoffRes.data ? (
        <motion.div
          key="handoff"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <HandoffCard
            handoff={handoffRes.data}
            onConsume={() => void consumeHandoff()}
            consuming={consumingHandoff}
          />
        </motion.div>
      ) : priority === 'digest' && digestRes.status === 'ok' && digestRes.data ? (
        <motion.div
          key="digest"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <WeeklyDigest
            data={digestRes.data}
            honestDay={honestDay === true}
            onCheckIn={!checkInDone ? () => navigate('/app/checkin') : undefined}
            onOpenInsights={() => navigate('/app/insights')}
          />
        </motion.div>
      ) : priority === 'checkin' ? (
        <motion.div
          key="checkin"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <TonightsCheckInCard onStart={() => navigate('/app/checkin')} />
        </motion.div>
      ) : priority === 'pawsTomorrow' && pawsTomorrow && tomorrowDayIndex != null ? (
        <motion.div
          key="paws-tomorrow"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <PAWSTomorrowCard
            week={pawsTomorrow}
            daysIn={tomorrowDayIndex + 1}
            onOpenMindTricks={() => navigate('/app/mind-tricks')}
          />
        </motion.div>
      ) : priority === 'nudge' ? (
        <motion.div
          key="nudge"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
        >
          <AINudgeCard
            loading={nudgeRes.status === 'loading'}
            message={nudgeRes.status === 'ok' ? (nudgeRes.data?.message ?? null) : null}
            cta={
              <Button variant="tertiary" onClick={() => navigate('/app/checkin')}>
                Start check-in →
              </Button>
            }
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  ) : null;

  // Feel-check follow-up prompt after a prior dose.
  const feelCheck =
    followupsRes.status === 'ok' && followupsRes.data.length > 0 ? (
      <FeelCheckBanner
        key={followupsRes.data[0].id}
        pending={followupsRes.data[0]}
        onAnswered={() => followupsRes.refresh()}
      />
    ) : null;

  // Milestone celebration card.
  const milestone = !isFirstRun ? (
    <MilestoneCard
      streak={streakRes.status === 'ok' ? streakRes.data : null}
      plan={taperRes.status === 'ok' ? taperRes.data : null}
    />
  ) : null;

  // Predicted-window chip — surfaces only when the next forecasted
  // danger window is inside the 6h horizon. Tap deeplinks urge-surf.
  const dangerChip =
    !isFirstRun &&
    dangerWindowRes.status === 'ok' &&
    dangerWindowRes.data.withinSixHours &&
    dangerWindowRes.data.window ? (
      <PredictedWindowChip
        hour={dangerWindowRes.data.window.hour}
        label={dangerWindowRes.data.window.label}
        onOpenUrgeSurf={() => navigate('/app/urge?phase=pre&trigger=predicted_window')}
      />
    ) : null;

  // Quick log — preset dose chips; the fast path beside the full sheet.
  const quickLogSection = !isFirstRun ? (
    <div id="quick-log">
      <h2 className="text-h2" style={{ marginBlockEnd: 'var(--space-3)' }}>
        Quick log
      </h2>
      <p className="muted text-caption" style={{ marginBlockEnd: 'var(--space-2)' }}>
        Tap and hold to log — a small pause before each dose.
      </p>
      <div className="scroll-strip">
        {lastDose ? (
          <DosePresetChip
            label={`Same as last · ${lastDose.amount} ${
              lastDose.form === 'tablet_7oh' ? 'mg' : lastDose.form === 'leaf' ? 'g' : 'mL'
            }`}
            onClick={() => void sameAsLast()}
            holdToConfirm
          />
        ) : null}
        {COMMON_TABLET_STRENGTHS_MG.slice(0, 5).map((mg) => (
          <DosePresetChip key={mg} label={`${mg} mg`} onClick={() => quickLog(mg)} holdToConfirm />
        ))}
        <button
          type="button"
          className="dose-chip"
          onClick={() => navigate({ pathname: '/app', hash: '#log' })}
        >
          <Icon icon={Pill} size={16} />
          Custom →
        </button>
      </div>
    </div>
  ) : null;

  // Today's logged doses.
  const dosesList = !isFirstRun ? (
    <div>
      <h2 className="text-h2" style={{ marginBlockEnd: 'var(--space-3)' }}>
        Today's doses
      </h2>
      {(() => {
        // Hide pending-delete rows so the optimistic UI exits
        // immediately on tap. If the user hits Undo, the id is
        // removed from the set and the row re-appears.
        const visibleDoses =
          dosesRes.status === 'ok' ? dosesRes.data.filter((d) => !pendingDeleteIds.has(d.id)) : [];
        if (dosesRes.status === 'error') {
          return <ApiErrorCard error={dosesRes.error} onRetry={dosesRes.refresh} compact />;
        }
        if (dosesRes.status === 'ok' && visibleDoses.length === 0) {
          return (
            <EmptyState
              icon={Sunrise}
              title="Fresh page."
              body="Nothing logged yet — that's the morning starting where you left it."
            />
          );
        }
        if (dosesRes.status === 'ok') {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <AnimatePresence>
                {visibleDoses.map((d) => (
                  <DoseChip key={d.id} dose={d} onDelete={() => void deleteDose(d.id)} />
                ))}
              </AnimatePresence>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Skeleton width={84} height={32} radius="var(--radius-pill, 9999px)" />
            <Skeleton width={84} height={32} radius="var(--radius-pill, 9999px)" />
            <Skeleton width={84} height={32} radius="var(--radius-pill, 9999px)" />
          </div>
        );
      })()}
    </div>
  ) : null;

  // Talk-me-down — crisis affordance.
  const talkMeDown = !isFirstRun ? (
    <TalkMeDownCard
      onOpenChat={() => navigate('/app/chat')}
      onLongPress={() => setCrisisOpen(true)}
    />
  ) : null;

  // Embedded SOS toolkit card (replaces the old global FAB). Hidden via
  // Settings → Help & appearance.
  const sosToolkit = !isFirstRun && me?.settings.sosCardHidden !== true ? <SosToolkitCard /> : null;

  // Bedtime pledge — evening/night only, when no pledge exists for today.
  const bedtimePledge =
    !isFirstRun && showBedtimePledge ? (
      <BedtimePledgeCard
        onSaved={() => todayPledgeRes.refresh()}
        onHandoffSaved={() => handoffRes.refresh()}
      />
    ) : null;

  // Zone C — collapsible tools drawer.
  const toolsDrawer = !isFirstRun ? (
    <details
      className="tools-drawer"
      open={toolsOpen}
      onToggle={(e) => setToolsOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="tools-drawer-summary">
        <span className="tools-drawer-title">Tools</span>
        <Icon icon={ChevronDown} className="tools-drawer-chevron" size={18} />
      </summary>
      <div className="tools-drawer-body">
        <ListItemCard
          icon={Waves}
          iconTone="accent"
          title="I'm having an urge"
          caption="A 4-minute mindful timer. Stay with it."
          onClick={() => navigate('/app/urge')}
          trailing={<span aria-hidden="true">→</span>}
        />
        {/* Phase D.1 §P10.9 — Rough-Night soften toggle. Kept inside
                  the Tools drawer so it's one tap away on a tough night
                  but stays out of the primary flow on normal days. */}
        <RoughNightToggle />
        <ListItemCard
          icon={Brain}
          iconTone="primary"
          title="Mind tricks"
          caption="ACT defusion · MBRP scripts."
          onClick={() => navigate('/app/mind-tricks')}
          trailing={<span aria-hidden="true">→</span>}
        />
        <ListItemCard
          icon={LineChart}
          iconTone="primary"
          title="Trends and insights"
          caption="SOWS, mood, sleep, cravings."
          onClick={() => navigate('/app/insights')}
          trailing={<span aria-hidden="true">→</span>}
        />
        <ListItemCard
          icon={BookOpen}
          iconTone="primary"
          title="Knowledge base"
          caption="Ask anything · 7-OH, taper, treatment, laws."
          onClick={() => navigate('/app/learn')}
          trailing={<span aria-hidden="true">→</span>}
        />
        <ListItemCard
          icon={NotebookPen}
          iconTone="primary"
          title="Journal"
          caption="Long-form, as much as you want."
          onClick={() => navigate('/app/journal')}
          trailing={<span aria-hidden="true">→</span>}
        />
        <ListItemCard
          icon={Search}
          iconTone="primary"
          title="Search history"
          caption="Find any dose, check-in, or journal entry."
          onClick={() => navigate('/app/history')}
          trailing={<span aria-hidden="true">→</span>}
        />
        {pawsWeek && taperDayIndex != null ? (
          <PAWSWeekCard week={pawsWeek} daysIn={taperDayIndex + 1} />
        ) : null}
        {/* PR 1C — Day 30+ "what's normal right now" surface.
                  Renders only when dayIndex >= 30 (the component handles
                  the gate internally). */}
        <PAWSPhaseCard dayIndex={taperDayIndex} />
        {/* PR 3A — symptom-aware 21-day projection. Renders only
                  when the user has a taper plan (component handles the
                  null gate internally). */}
        <WithdrawalProjectionCard dayIndex={taperDayIndex} />
        {/* Gap 4 — per-form PK context for the user's most recent dose.
                  Renders only when there's a dose <24h old; component
                  short-circuits to null otherwise. Especially useful
                  during a tablet → powder transition where the felt
                  window math is dramatically different. */}
        <DoseTimingCard
          lastDose={
            dosesRes.status === 'ok' && dosesRes.data.length > 0
              ? dosesRes.data[0]
              : null
          }
        />
        {/* PR 2C — harm-reduction surface, collapsible default-closed.
                  Always renders; copy framed as preparation, not permission. */}
        <RelapsePreventionCard />
        <StoryOfDayCard />
        <ListItemCard
          icon={Activity}
          iconTone="neutral"
          title="I had a setback"
          caption="Setbacks are data, not failure."
          onClick={() => {
            // Clear the auto-show dedupe so the sheet opens even
            // if the user already saw it today.
            try {
              window.localStorage.removeItem(`qk.lapseShown.${today}`);
            } catch {
              // ignored
            }
            setLapseOpen(true);
          }}
          trailing={<span aria-hidden="true">→</span>}
        />
        {/* Phase 2 — install on home screen. Renders only when we
                  actually have a path forward (captured prompt event OR
                  iOS Safari outside standalone). Single-shot: once the
                  user prompts/dismisses, `canInstall` flips back to
                  false until the browser re-fires `beforeinstallprompt`. */}
        {install.canInstall ? (
          <ListItemCard
            icon={Download}
            iconTone="accent"
            title="Install on home screen"
            caption="One tap from the lock screen. No app store needed."
            onClick={() => {
              void (async () => {
                const outcome = await install.promptInstall();
                if (outcome === 'accepted') {
                  toast.push('Installed. Welcome home.', { tone: 'success' });
                }
              })();
            }}
            trailing={<span aria-hidden="true">→</span>}
          />
        ) : canShowIosInstall ? (
          <ListItemCard
            icon={Download}
            iconTone="accent"
            title="Install on home screen"
            caption="Tap the Share button, then 'Add to Home Screen.'"
            onClick={() => {
              toast.push("Tap the Share button below, then 'Add to Home Screen.'", {
                durationMs: 6000,
              });
            }}
            trailing={<span aria-hidden="true">→</span>}
          />
        ) : null}
      </div>
    </details>
  ) : null;

  // ===== Assemble the home screen =====
  // First-run keeps the current tree; mobile is the usage-ranked
  // reorder; desktop keeps the 2-column `today-zones` grid.
  const screenBody = isFirstRun ? (
    <>
      <div>
        {zoneAStatus}
        {moodSleep}
        {whyI}
      </div>
      {feelCheck}
    </>
  ) : isMobile ? (
    <>
      {statusSlot}
      {recoveryForecastCard}
      {quickLogSection}
      {dosesList}
      {zoneB}
      {dangerChip}
      {feelCheck}
      {moodSleep}
      {talkMeDown}
      {sosToolkit}
      {milestone}
      {recoveryCard}
      {howTodayDetails}
      {whyI}
      {tomorrowPlan}
      {insightsCard}
      {bedtimePledge}
      {toolsDrawer}
    </>
  ) : (
    <>
      {tomorrowPlan}
      <div className="today-zones">
        {zoneAStatus}
        {moodSleep}
        {whyI}
        {zoneB}
      </div>
      {recoveryForecastCard}
      {feelCheck}
      {milestone}
      {dangerChip}
      {quickLogSection}
      {dosesList}
      {talkMeDown}
      {sosToolkit}
      {bedtimePledge}
      {toolsDrawer}
    </>
  );

  return (
    <>
      {bannerVisible ? (
        <CrisisBanner
          onOpen={() => setCrisisOpen(true)}
          onSnooze={() => void snoozeBanner()}
          snoozing={snoozingBanner}
        />
      ) : null}
      <PageContainer ref={scrollRef}>
        {ptrSpinner}
        <ScreenHeader title={greeting} subtitle={fmtDate(new Date().toISOString())} />
        {screenBody}
        {/* Crisis affordance is handled by CrisisBanner at the top and
            the persistent /app/crisis route — one per screen, per
            audit-m1 §1. */}
      </PageContainer>

      <CrisisModal open={crisisOpen} onDismiss={() => setCrisisOpen(false)} />
      <LapseRecoverySheet open={lapseOpen} onOpenChange={setLapseOpen} />
    </>
  );
}

// Narrow-viewport check, kept local so we don't leak a one-use hook
// into src/hooks. The breakpoint mirrors `--bp-md-sm` (414 px) — phones
// in portrait. On SSR / non-window environments it returns false so
// the desktop default (expanded) is rendered.
function useIsMobile(maxWidthPx = 414): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Safari < 14 only supports addListener.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidthPx]);
  return isMobile;
}

// TomorrowPlanCollapsible — wraps TomorrowPlanCard in a `<details>` on
// mobile (≤414 px), defaulting to closed. The `<summary>` shows a
// one-line "Tomorrow: NNN mg · K doses" preview so the user has the
// at-a-glance signal without losing the chip strip + disclaimer that
// live inside the card. On desktop the card renders inline as before.
//
// Implementation notes:
//   - We call `useTomorrowPlan()` here for the summary copy. The
//     TomorrowPlanCard renders inside `<details>` and also calls the
//     same hook; the GET dedupes at the browser HTTP cache and the
//     extra render cost is negligible.
//   - Self-hides on 404 / error (matches TomorrowPlanCard behaviour)
//     so the user doesn't see an empty expander shell.
//   - The summary row meets WCAG 2.5.5 (≥44 CSS px tall) via the
//     scoped `.tomorrow-plan-collapsible__summary` rule.
function TomorrowPlanCollapsible() {
  const isMobile = useIsMobile();
  const tomorrow = useTomorrowPlan();

  // On desktop, render the card inline — no collapse needed.
  if (!isMobile) return <TomorrowPlanCard />;

  // Match TomorrowPlanCard self-hide behaviour: hide on 404 / error
  // and during loading we still render the card (which shows its own
  // skeleton) so the slot doesn't pop in late.
  if (tomorrow.status === 'error') return null;
  if (tomorrow.status === 'idle' || tomorrow.status === 'loading') {
    return <TomorrowPlanCard />;
  }

  const { data } = tomorrow;
  const slotCount = data.suggestedSchedule.length;
  const targetLabel =
    Math.round(data.targetMg) === data.targetMg ? String(data.targetMg) : data.targetMg.toFixed(1);
  const slotSuffix = slotCount > 0 ? ` · ${slotCount} dose${slotCount === 1 ? '' : 's'}` : '';

  return (
    <details className="tomorrow-plan-collapsible">
      <summary className="tomorrow-plan-collapsible__summary">
        <span className="tomorrow-plan-collapsible__label">
          Tomorrow: {targetLabel} mg{slotSuffix}
        </span>
        <Icon icon={ChevronDown} size={18} className="tomorrow-plan-collapsible__chevron" />
      </summary>
      <div className="tomorrow-plan-collapsible__body">
        <TomorrowPlanCard />
      </div>
    </details>
  );
}
